import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import {
  AccountService,
  ContextService,
  EventsSequenceExtended,
  EventsSequenceExtendedService,
  ScheduleService,
} from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { addMonths, startOfWeek } from 'date-fns';
import {
  Classroom,
  EventStatusEnum,
  GenerationStatusEnum,
  Meeting,
  Router as ContentRouter,
  SingleEvent,
  User,
  UserService,
  UserServiceScheduleRequest,
  UserServiceScheduleResponse,
  WeekdaysEnum,
} from 'lingo2-models';
import { switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-user-service-debug-schedule',
  templateUrl: './user-service-debug-schedule.component.html',
  styleUrls: ['./user-service-debug-schedule.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserServiceDebugScheduleComponent extends ChangableComponent implements OnInit {
  private contextService = inject(ContextService);
  private scheduleService = inject(ScheduleService);
  private eventsSequenceExtendedService = inject(EventsSequenceExtendedService);

  @Input()
  public set userService(userService: UserService) {
    const changed = this._userService?.id !== userService?.id;
    this._userService = userService;
    if (changed) {
      this.loadSchedule(this._userService.author_id, this._userService.id);
    }
  }
  public getUserFullName = AccountService.getUserFullName;

  private _serviceEvents: EventsSequenceExtended[] = [];
  private _userService: UserService;
  private me: User;

  public ngOnInit() {
    this.watchMe$.pipe(tap(), takeUntil(this.destroyed$)).subscribe({
      next: () => this.detectChanges(),
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
  }

  public reload() {
    this.loadSchedule(this._userService.author_id, this._userService.id);
  }

  public get isSchoolService(): boolean {
    return !!this._userService?.school_id;
  }

  public weekdays(event: EventsSequenceExtended): string[] {
    return (event.user_service?.options?.weekdays || []).map((w) => WeekdaysEnum[w]);
  }

  public generationStatusName(status: GenerationStatusEnum): string {
    return status ? GenerationStatusEnum[status] || '?' + status.toString() : 'none';
  }

  public statusName(status: EventStatusEnum): string {
    return status ? EventStatusEnum[status] || '?' + status.toString() : 'none';
  }

  public seqEventCssClass(event: SingleEvent): string[] {
    const classes = [];
    if (event.status) {
      classes.push(EventStatusEnum[event.status]);
    }
    if (event.generation_status) {
      classes.push(GenerationStatusEnum[event.generation_status]);
    }
    return classes;
  }

  public get serviceEvents(): EventsSequenceExtended[] {
    return this._serviceEvents;
  }

  public classroomRoute(classroom: Classroom): string[] {
    return ContentRouter.classroomRoute(classroom);
  }

  public meetingRoute(meeting: Meeting): string[] {
    return ContentRouter.meetingRoute(meeting);
  }

  protected loadSchedule(teacher_id: string, user_service_id: string) {
    const request = new UserServiceScheduleRequest({
      date_from: startOfWeek(new Date()),
      date_to: addMonths(startOfWeek(new Date()), 3),
      user_service_id,
      teacher_id,
      student_id: null, // ? this.me.id,
    });
    this.scheduleService
      .findServiceSignupSchedule(request)
      .pipe(
        switchMap((response: UserServiceScheduleResponse) =>
          this.eventsSequenceExtendedService.extend$(response.service_events, ['meeting']),
        ),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (service_events) => {
          this._serviceEvents = service_events;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  protected get watchMe$() {
    return this.contextService.me$.pipe(tap((me) => (this.me = me)));
  }

  public trackByFn(index) {
    return index;
  }
}
